import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  ChevronLeft, 
  ChevronRight, 
  Zap,
  HelpCircle,
  Users,
  BookOpen,
  DollarSign,
  Building2,
  PieChart,
  Info,
  Wand2,
  GitFork,
  Network,
  CalendarDays
} from 'lucide-react';
import { useTheme } from './ThemeProvider';
import LogoSvg from './LogoSvg';

export const menuItems = [
  { icon: Home, label: 'Home', path: '/', isExternal: false },
  { icon: PieChart, label: 'Solutions', path: '/solutions', isExternal: false },
  { icon: Building2, label: 'Enterprise', path: '/enterprise', isExternal: false },
  { icon: BookOpen, label: 'Blog', path: '/blog', isExternal: false },
  { icon: Network, label: 'Open Source RL', path: '/open-source-rl', isExternal: false },
  { icon: Wand2, label: 'Fine-Tuning', path: '/fine-tuning', isExternal: false },
  { icon: Info, label: 'About', path: '/about', isExternal: false }
];

interface SidebarItemProps {
  icon: React.ElementType;
  label: string;
  path: string;
  active?: boolean;
  collapsed?: boolean;
  isExternal?: boolean;
  onClick?: () => void;
}

const SidebarItem = ({ icon: Icon, label, path, active, collapsed, isExternal = false, onClick }: SidebarItemProps) => {
  const linkProps = isExternal ? {
    href: path,
    target: "_blank",
    rel: "noopener noreferrer",
    as: "a"
  } : {};
  
  if (isExternal) {
    return (
      <a
        href={path}
        onClick={onClick}
        className={cn(
          "sidebar-nav-link w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
          active 
            ? "bg-primary/10" 
            : "hover:bg-secondary"
        )}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon size={collapsed ? 24 : 18} className={cn(
          collapsed ? "mx-auto" : "",
          active 
            ? "sidebar-icon-active" 
            : "sidebar-icon"
        )} />
        {!collapsed && <span className={active ? "sidebar-text-active" : "sidebar-text"}>{label}</span>}
      </a>
    );
  }
  
  return (
    <Link
      to={path}
      onClick={onClick}
      className={cn(
        "sidebar-nav-link w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        active 
          ? "bg-primary/10" 
          : "hover:bg-secondary"
      )}
    >
      <Icon size={collapsed ? 24 : 18} className={cn(
        collapsed ? "mx-auto" : "",
        active 
          ? "sidebar-icon-active" 
          : "sidebar-icon"
      )} />
      {!collapsed && <span className={active ? "sidebar-text-active" : "sidebar-text"}>{label}</span>}
    </Link>
  );
};

interface DashboardSidebarProps {
  className?: string;
}

const DashboardSidebar = ({ className }: DashboardSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeItem, setActiveItem] = useState('');
  const location = useLocation();
  const { theme } = useTheme();
  
  const isDarkTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') {
      setActiveItem('Home');
      return;
    }
    
    const currentMenuItem = menuItems.find(item => {
      if (item.path === '/') return false;
      return path.startsWith(item.path);
    });

    if (currentMenuItem) {
      setActiveItem(currentMenuItem.label);
    } else {
      setActiveItem('');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/') {
      setActiveItem('Home');
    }
  }, []);

  useEffect(() => {
    const handleSetActiveItem = (event: CustomEvent) => {
      setActiveItem(event.detail);
    };

    document.addEventListener('setActiveItem', handleSetActiveItem as EventListener);
    
    return () => {
      document.removeEventListener('setActiveItem', handleSetActiveItem as EventListener);
    };
  }, []);

  console.log('Active Item:', activeItem, 'Current Path:', location.pathname);

  return (
    <div className={cn(
      "relative hidden md:flex flex-col h-screen border-r border-border bg-sidebar transition-all duration-300",
      collapsed ? "w-[70px]" : "w-[240px]",
      className
    )}>
      <div className="p-4 flex items-center justify-between border-b border-border h-16">
        <div className="flex items-center overflow-hidden">
          <LogoSvg collapsed={collapsed} className="flex-shrink-0" />
        </div>
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "text-foreground/70 hover:text-foreground rounded-md p-1",
            collapsed ? "absolute right-[-10px] z-10 bg-background shadow-sm border border-border rounded-full" : ""
          )}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-none py-4 px-3 space-y-1">
        {menuItems.map((item) => (
          <div key={item.label} className="group">
            <SidebarItem
              icon={item.icon}
              label={item.label}
              path={item.path}
              active={activeItem === item.label}
              collapsed={collapsed}
              isExternal={item.isExternal}
              onClick={() => !item.isExternal && setActiveItem(item.label)}
            />
          </div>
        ))}
      </div>

      <div className="border-t border-border p-4">
        {!collapsed && (
          <a 
            href="mailto:hello@openpipe.ai" 
            className="sidebar-nav-link flex items-center gap-3 cursor-pointer"
            target="_blank" 
            rel="noopener noreferrer"
          >
            <HelpCircle size={18} className="sidebar-icon" />
            <span className="sidebar-text text-sm">Help & Support</span>
          </a>
        )}
        {collapsed && (
          <a 
            href="mailto:hello@openpipe.ai" 
            className="sidebar-nav-link flex justify-center cursor-pointer"
            target="_blank" 
            rel="noopener noreferrer"
          >
            <HelpCircle size={24} className="sidebar-icon" />
          </a>
        )}
      </div>
    </div>
  );
};

export default DashboardSidebar;
